import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { setRanchTimeZone } from '../components/ranchTime'
import { useQueryClient } from '@tanstack/react-query'
import { useMe } from '../api/hooks'
import { ApiError } from '../api/client'
import type { Me, Membership, Role } from '../api/types'
import { clearLocal, readLocal, writeLocal } from '../data/browser'
import { clearReference } from '../data/reference'
import type { BootstrapResult } from './bootstrap'
import { isKeycloakConfigured, logout } from './keycloak'
import { getScope, setScope, subscribeScope, type Scope } from './scope'

/*
  dataroom's AuthContext, with the active ranch (ADR 0020: no offline mode):

  - `mode` is the bootstrap branch the app started in.
  - `me` comes from GET /me.
  - The active scope (organization + ranch) lives in scope.ts so the API client and the
    read model can read it outside the tree; this context chooses it and remembers it in
    this browser.
*/

export interface AuthState {
  mode: BootstrapResult['mode']
  me: Me | null
  memberships: Membership[]
  roles: Role[]
  loading: boolean
  error: Error | null
  correlationId: string | null
  /** False in stub mode, where there is no session to end. */
  canSignOut: boolean
  signOut: () => void
  scope: Scope | null
  activeMembership: Membership | null
  switchRanch: (membership: Membership) => void
}

const AuthContext = createContext<AuthState | null>(null)

const SCOPE_KEY = 'scope'

function membershipScope(m: Membership): Scope | null {
  return m.ranch ? { organizationId: m.organization.publicId, ranchId: m.ranch.publicId } : null
}

export function AuthProvider({
  result,
  children,
}: {
  result: BootstrapResult
  children: ReactNode
}) {
  const query = useMe()
  const queryClient = useQueryClient()
  const scope = useSyncExternalStore(subscribeScope, getScope, getScope)
  const me: Me | null = query.data?.data ?? null

  // Choose the active scope: the one remembered in this browser if the user still holds
  // it, else the first ranch membership.
  useEffect(() => {
    if (!me || getScope()) return
    const saved = readLocal<Scope>(SCOPE_KEY)
    const holds = (s: Scope) =>
      me.memberships.some(
        (m) => m.organization.publicId === s.organizationId && m.ranch?.publicId === s.ranchId,
      )
    if (saved && holds(saved)) {
      setScope(saved)
      return
    }
    const first = me.memberships.map(membershipScope).find((s): s is Scope => s !== null)
    if (first) {
      setScope(first)
      writeLocal(SCOPE_KEY, first)
    }
  }, [me])

  const switchRanch = useCallback(
    (membership: Membership) => {
      const next = membershipScope(membership)
      if (!next) return
      setScope(next)
      writeLocal(SCOPE_KEY, next)
      // Ranch-scoped queries belong to the old ranch; drop them rather than show them.
      void queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
    [queryClient],
  )

  const signOut = useCallback(() => {
    // Sign-out forgets what this browser held (brief §8.2), then ends the Keycloak session.
    clearReference()
    clearLocal()
    queryClient.clear()
    setScope(null)
    logout()
    if (!isKeycloakConfigured()) window.location.assign('/')
  }, [queryClient])

  const value = useMemo<AuthState>(() => {
    const activeMembership =
      (scope &&
        me?.memberships.find(
          (m) =>
            m.organization.publicId === scope.organizationId && m.ranch?.publicId === scope.ranchId,
        )) ??
      null
    // Before any child renders: forms default to the ranch's date and show its times (ranchTime.ts).
    setRanchTimeZone(activeMembership?.ranch?.timeZone)
    return {
      mode: result.mode,
      me,
      memberships: me?.memberships ?? [],
      roles: me?.user.roles ?? [],
      loading: query.isPending && !me,
      error: query.error,
      correlationId:
        query.data?.correlationId ??
        (query.error instanceof ApiError ? query.error.correlationId : null),
      canSignOut: isKeycloakConfigured() || Boolean(me),
      signOut,
      scope,
      activeMembership,
      switchRanch,
    }
  }, [result.mode, me, query.isPending, query.error, query.data, signOut, scope, switchRanch])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const state = useContext(AuthContext)
  if (state === null) {
    throw new Error('useAuth must be used inside AuthProvider.')
  }
  return state
}
